import { useState } from 'react';
import { toast } from 'sonner';
import { MoreVertical } from 'lucide-react';
import { useUpdateLevelEntity, useDeleteLevelEntity } from '../../../../hooks/useLevelEntities';
import { handleApiError } from '../../../../api/errorMapping';
import { useAuth } from '../../../../hooks/useAuth';
import { Gate } from '../../../../components/permissions/Gate';
import { ConfirmDialog } from '../../../../components/shared/ConfirmDialog';
import {
    DropdownMenu,
    DropdownMenuTrigger,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
} from '../../../../components/ui/dropdown-menu';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from '../../../../components/ui/dialog';
import { Button } from '../../../../components/ui/button';
import { Input } from '@fiado/react-kit';
import type {
    LevelEntityListItemLocal,
    LevelNameLocal,
} from '../../../../api/types/LevelEntityLocal';

interface Props {
    entity: LevelEntityListItemLocal;
    /** tenantId del DRILL-DOWN (param de ruta), NO el del user logueado. */
    tenantId: string;
    level: LevelNameLocal;
    /** Nombre legible del nivel (de la app del tenant). Para títulos/mensajes — NUNCA "LEVEL_1" crudo. */
    levelDisplayName: string;
}

/**
 * Menú "⋮" por fila de entidad de nivel. Espeja `PlatformRetailerRowActions` pero sobre los hooks
 * genéricos `useUpdate/DeleteLevelEntity` (que ya invalidan la query del nivel). El contrato del
 * nivel solo permite editar `displayName` (`UpdateLevelEntityRequest`) y borrar (DELETE →
 * `{ deleted: true }`). Gateado por `tenant.level.manage` (scope TENANT/tenantId; el platform admin
 * lo cubre por rank; el backend re-enforza).
 */
export function LevelEntityRowActions({ entity, tenantId, level, levelDisplayName }: Props) {
    const { hasPermission, hasScopeAccess } = useAuth();
    const [editOpen, setEditOpen] = useState(false);
    const [editName, setEditName] = useState(entity.displayName);
    const [confirmDelete, setConfirmDelete] = useState(false);

    const updateMut = useUpdateLevelEntity(tenantId, level);
    const deleteMut = useDeleteLevelEntity(tenantId, level);

    // Sin scope sobre el tenant del path o sin el permiso de gestión no mostramos el "⋮" (cada item
    // igual se gatea con <Gate>). Espejo de PlatformRetailerRowActions.
    if (!hasScopeAccess('TENANT', tenantId) || !hasPermission('tenant.level.manage')) {
        return null;
    }

    return (
        <>
            {/* modal={false}: evita el page-lock de Radix (pointer-events:none) cuando la fila se
                desmonta tras confirmar una acción (DEC-009). */}
            <DropdownMenu modal={false}>
                <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="sm" aria-label="Acciones">
                        <MoreVertical className="size-4" />
                    </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                    <Gate permission="tenant.level.manage" scope="TENANT" scopeRef={tenantId}>
                        <DropdownMenuItem
                            onClick={() => {
                                setEditName(entity.displayName);
                                setEditOpen(true);
                            }}
                        >
                            Editar
                        </DropdownMenuItem>
                    </Gate>
                    <DropdownMenuSeparator />
                    <Gate permission="tenant.level.manage" scope="TENANT" scopeRef={tenantId}>
                        <DropdownMenuItem
                            className="text-destructive focus:text-destructive"
                            onClick={() => {
                                setConfirmDelete(true);
                            }}
                        >
                            Eliminar
                        </DropdownMenuItem>
                    </Gate>
                </DropdownMenuContent>
            </DropdownMenu>

            <ConfirmDialog
                open={confirmDelete}
                onOpenChange={setConfirmDelete}
                title={`Eliminar ${levelDisplayName.toLowerCase()}`}
                description={`${entity.displayName} se eliminará. ¿Continuar?`}
                confirmLabel="Eliminar"
                destructive
                onConfirm={() => {
                    deleteMut.mutate(entity.id, {
                        onSuccess: () => {
                            toast.success(`${levelDisplayName} eliminado`);
                            setConfirmDelete(false);
                        },
                        onError: handleApiError,
                    });
                }}
            />

            <Dialog open={editOpen} onOpenChange={setEditOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Editar {levelDisplayName.toLowerCase()}</DialogTitle>
                        <DialogDescription>{entity.displayName}</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                        <Input
                            label="Nombre"
                            value={editName}
                            onChange={(e) => {
                                setEditName(e.target.value);
                            }}
                        />
                    </div>
                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => {
                                setEditOpen(false);
                            }}
                        >
                            Cancelar
                        </Button>
                        <Button
                            disabled={updateMut.isPending || !editName.trim()}
                            onClick={() => {
                                updateMut.mutate(
                                    { id: entity.id, body: { displayName: editName.trim() } },
                                    {
                                        onSuccess: () => {
                                            toast.success(`${levelDisplayName} actualizado`);
                                            setEditOpen(false);
                                        },
                                        onError: handleApiError,
                                    },
                                );
                            }}
                        >
                            Guardar
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
}
